import { ContractorVisitStatusBadge } from "@/components/ContractorVisitStatusBadge";
import DescriptionList from "@/components/description-list/DescriptionList";
import { Link } from "@/components/link";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Module,
  PATH_PROJECT_ROLE_BASED_ASSIGNMENTS,
  PATH_PROJECT_WITH_ID,
  PATH_STORE,
} from "@/constants";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { useFormatters } from "@/lib/hooks/useFormatters";
import { formatNumber } from "@/lib/utils/formatters";
import { VisitStatus } from "@/schemas/contractorVisit";
import { ReadContractorVisit } from "@/types/contractorVisit";
import { FolderClockIcon, UserIcon } from "lucide-react";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { generatePath } from "react-router-dom";

interface ContractorVisitDetailsProps {
  visitData: ReadContractorVisit | undefined;
}

export const ContractorVisitDetails = memo(
  ({ visitData }: ContractorVisitDetailsProps) => {
    const { t } = useTranslation();
    const { formatDateTime } = useFormatters();
    const permissions = useModulePermissions(Module.ContractorVisits);
    const hasFullAccess = permissions.contractor_visits_read_full;
    const projectId = visitData?.project_id as string;
    const hasMissingContractor = visitData?.contractor_id === null;

    return (
      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="text-lg font-medium">
            {t("projectContractorVisits.title.single")}
          </CardTitle>
          {!hasMissingContractor && (
            <p className="text-3xl flex items-center font-bold gap-3">
              <UserIcon className="shrink-0" />
              {visitData?.contractor_full_name_and_personal_id_number}
            </p>
          )}
        </CardHeader>
        <CardContent>
          {hasMissingContractor && (
            <Alert variant="warning" className="mb-5">
              <AlertTitle>
                {t("projectContractorVisits.missingContractor.title")}
              </AlertTitle>
              <AlertDescription>
                <p className="my-2">
                  {t("projectContractorVisits.missingContractor.description")}
                </p>
                <Button asChild variant="outline">
                  <Link
                    to={generatePath(PATH_PROJECT_ROLE_BASED_ASSIGNMENTS, {
                      projectId,
                    })}
                  >
                    <FolderClockIcon className="w-4 h-4" />
                    {t("projects.navigation.assignContractors")}
                  </Link>
                </Button>
              </AlertDescription>
            </Alert>
          )}
          <DescriptionList
            data={visitData || {}}
            translationKey="projectContractorVisits"
            variant="ghost"
          >
            <DescriptionList.Content className="p-0 grid grid-cols-1 md:grid-cols-2 gap-4">
              <DescriptionList.Link
                linkClassName="max-w-xs truncate"
                name="project_name"
                to={generatePath(PATH_PROJECT_WITH_ID, {
                  projectId: visitData?.project_id || "",
                })}
              />
              <DescriptionList.Link
                linkClassName="max-w-xs truncate"
                name="store_name"
                to={generatePath(PATH_STORE, {
                  storeId: visitData?.store_id || "",
                })}
              />
              <DescriptionList.Item name="project_role_name" />
              <DescriptionList.Item
                name="status"
                renderer={(value) => (
                  <ContractorVisitStatusBadge status={value as VisitStatus} />
                )}
              />
              <DescriptionList.Item
                name="settlement_type"
                renderer={(value) => t(`settlementType.${value}`)}
              />
              <DescriptionList.Item name="date" />
              <DescriptionList.Item
                name="confirmation_user_name"
                renderer={(value) =>
                  value || t("projectContractorVisits.notConfirmed")
                }
              />
            </DescriptionList.Content>
          </DescriptionList>

          <hr className="my-6" />

          <DescriptionList
            data={visitData || {}}
            translationKey="projectContractorVisits"
            variant="ghost"
          >
            <DescriptionList.Content className="p-0 grid grid-cols-1 md:grid-cols-3 gap-4">
              <DescriptionList.Item name="suggested_start_time" />
              <DescriptionList.Item name="suggested_end_time" />
              {hasFullAccess && (
                <>
                  <DescriptionList.Item
                    name="pr_planned_duration"
                    renderer={(value) => formatNumber(value)}
                  />
                  <DescriptionList.Item
                    name="start_datetime"
                    renderer={(value) => formatDateTime(value)}
                  />
                  <DescriptionList.Item
                    name="end_datetime"
                    renderer={(value) => formatDateTime(value)}
                  />
                  <DescriptionList.Item
                    name="planned_duration"
                    renderer={(value) => formatNumber(value)}
                  />
                  <DescriptionList.Item
                    name="kam_planned_duration"
                    renderer={(value) => formatNumber(value)}
                  />
                  <DescriptionList.Item
                    name="real_duration"
                    renderer={(value) => formatNumber(value)}
                  />
                </>
              )}
              <DescriptionList.Item
                name="started_at"
                renderer={(value) => formatDateTime(value)}
              />
              <DescriptionList.Item
                name="ended_at"
                renderer={(value) => formatDateTime(value)}
              />
            </DescriptionList.Content>
          </DescriptionList>
        </CardContent>
      </Card>
    );
  },
);
